'use client';

import React, { useState, useMemo } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  LayoutDashboard,
  Package,
  ClipboardList,
  Zap,
  Map,
  GitCompareArrows,
  FileSignature,
  Smartphone,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { useCountry } from '@/context/CountryContext';
import { useLanguage } from '@/context/LanguageContext';
import { computeReconciliation, getHeldContractors } from '@/lib/reconciliation';
import { connectionsByRegion } from '@/lib/mock-data';
import {
  defaultRatioMap,
  defaultBlendWeights,
  defaultCommissioningLagDays,
} from '@/lib/reconciliation-config';

interface NavItem {
  href: string;
  labelKey: string;
  icon: React.ElementType;
}

const navItems: NavItem[] = [
  { href: '/', labelKey: 'nav.dashboard', icon: LayoutDashboard },
  { href: '/inventory', labelKey: 'nav.inventory', icon: Package },
  { href: '/requisitions', labelKey: 'nav.requisitions', icon: ClipboardList },
  { href: '/reconciliation', labelKey: 'nav.reconciliation', icon: GitCompareArrows },
  { href: '/connections-dashboard', labelKey: 'nav.connections', icon: Zap },
  { href: '/geo-map', labelKey: 'nav.geoMap', icon: Map },
  { href: '/contracts', labelKey: 'nav.contracts', icon: FileSignature },
  { href: '/mobile', labelKey: 'nav.mobile', icon: Smartphone },
];

export default function Sidebar() {
  const pathname = usePathname();
  const [collapsed, setCollapsed] = useState(false);
  const { requisitions, profile } = useCountry();
  const { t } = useLanguage();

  // Compute badge from current country's requisitions
  const cdApprovalCount = requisitions.filter(
    (r) => r.stage === 'chief_director_approval'
  ).length;

  // Compute held contractors count for reconciliation badge
  const heldCount = useMemo(() => {
    const result = computeReconciliation({
      requisitions,
      regional: connectionsByRegion,
      ratios: defaultRatioMap,
      weights: defaultBlendWeights,
      lagDays: defaultCommissioningLagDays,
    });
    return getHeldContractors(result.rows).length;
  }, [requisitions]);

  return (
    <aside
      className="flex flex-col flex-shrink-0 h-screen sticky top-0 transition-all duration-300 ease-in-out z-30"
      style={{
        width: collapsed ? '64px' : '240px',
        backgroundColor: 'var(--primary-dark)',
      }}
    >
      {/* Brand */}
      <div
        className="flex items-center h-16 px-4 border-b flex-shrink-0"
        style={{ borderColor: 'rgba(255,255,255,0.08)' }}
      >
        <div className="flex items-center gap-2.5 min-w-0">
          <AppLogo size={32} />
          {!collapsed && (
            <span className="text-white font-bold text-[15px] leading-tight truncate">
              WestTrack<span style={{ color: 'var(--accent)' }}>GIS</span>
            </span>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-2">
        {!collapsed && (
          <p
            className="text-[10px] font-semibold uppercase tracking-widest mb-3 px-2"
            style={{ color: 'var(--nav-muted)', letterSpacing: '0.12em' }}
          >
            {t('nav.navigation')}
          </p>
        )}
        <ul className="space-y-0.5">
          {navItems.map((item) => {
            const isActive = pathname === item.href;
            const IconComp = item.icon;
            const badge =
              item.href === '/requisitions'
                ? cdApprovalCount
                : item.href === '/reconciliation'
                ? heldCount
                : undefined;
            const label = t(item.labelKey);
            return (
              <li key={`nav-${item.href}`}>
                <Link
                  href={item.href}
                  className={`flex items-center gap-3 rounded-lg px-2.5 py-2.5 text-sm font-medium transition-all duration-150 relative group min-w-0 ${
                    isActive
                      ? 'text-white bg-white/10 border-l-[3px] border-accent'
                      : 'text-nav-muted hover:text-white hover:bg-white/5 border-l-[3px] border-transparent'
                  }`}
                  title={collapsed ? label : undefined}
                >
                  <IconComp
                    size={18}
                    strokeWidth={isActive ? 2 : 1.7}
                    className="flex-shrink-0"
                  />
                  {!collapsed && (
                    <span className="flex-1 truncate min-w-0">{label}</span>
                  )}
                  {!collapsed && badge !== undefined && badge > 0 && (
                    <span
                      className="text-[10px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
                      style={{
                        backgroundColor: 'var(--status-red)',
                        color: 'white',
                        minWidth: '18px',
                        textAlign: 'center',
                      }}
                    >
                      {badge}
                    </span>
                  )}
                  {collapsed && badge !== undefined && badge > 0 && (
                    <span
                      className="absolute top-1 right-1 w-2 h-2 rounded-full"
                      style={{ backgroundColor: 'var(--status-red)' }}
                    />
                  )}
                  {/* Tooltip for collapsed state */}
                  {collapsed && (
                    <span
                      className="absolute left-full ml-2 px-2 py-1 text-xs font-medium text-white rounded-md opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-150 whitespace-nowrap z-50"
                      style={{ backgroundColor: 'var(--primary)' }}
                    >
                      {label}
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* WAEP Geo Indicator */}
      {!collapsed && (
        <div
          className="mx-3 mb-3 rounded-lg px-3 py-3"
          style={{
            borderTop: '1px solid rgba(255,255,255,0.08)',
            backgroundColor: 'rgba(255,255,255,0.04)',
          }}
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="w-2 h-2 rounded-full bg-status-green animate-pulse flex-shrink-0" />
            <span
              className="text-[11px] font-medium truncate"
              style={{ color: 'var(--nav-muted)' }}
            >
              {profile.name}
            </span>
          </div>
          <p
            className="text-[10px] font-semibold truncate"
            style={{ color: 'var(--accent)' }}
          >
            {profile.utility} - {profile.program}
          </p>
        </div>
      )}

      {/* Collapse toggle */}
      <div
        className="px-3 pb-4 flex-shrink-0"
        style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
      >
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-medium transition-colors duration-150 hover:bg-white/10"
          style={{ color: 'var(--nav-muted)' }}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRight size={16} />
          ) : (
            <>
              <ChevronLeft size={16} />
              <span>{t('nav.collapse')}</span>
            </>
          )}
        </button>
      </div>
    </aside>
  );
}